import { z, registry } from "./registry.js";
import { Database } from "./database.types.js";
import { Metadata, Modify, MetadataItemZod } from "./utils.js";
import { OpenApiGeneratorV3, extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';

extendZodWithOpenApi(z as any);

export * from "./database.types.js";
export * from "./tables.js";
export * from "./utils.js";
export * from "./integrations.js";
export * from "./account.js";
export * from "./dashboard.js";
export * from "./automations.js";

export enum LogType {
  ViewDashboard = "view-dashboard",
  EditDashboard = "edit-dashboard",
  CreateWidget = "create-widget",
  DeleteWidget = "delete-widget",
  EditWidget = "edit-widget",
}
export type WidgetSettings = {
  css_id?: string;
  css_classnames?: string;
  link?: string;
};
export interface DatasourceFilter {
  column: string;
  type: 'text' | 'number' | 'date';
  label: string;
}

export type WidgetMessage = {
  role: "user" | "assistant";
  content: string;
};



export type Message = Modify<Database["public"]["Tables"]["messages"]["Row"], {
  role: "user" | "assistant" | "tool";
  content?: string;
  tool_calls?: any[];
}>;
export const MessageZod = registry.register("Message", (z.object({
  id: z.string().uuid().openapi({ description: "Message ID.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  content: z.string().optional().openapi({ description: "Message content.", example: "Hello!" }),
  created_at: z.string().openapi({ description: "Timestamp when the message was created.", example: "2024-05-01T16:58:00.000Z" }),
  dashboard: z.string().uuid().nullable().openapi({ description: "Dashboard ID associated with the message, if any.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  question: z.string().uuid().openapi({ description: "Question ID associated with the message.", example: "question_123" }),
  role: z.enum(["user", "assistant", "tool"]).nullable().openapi({ description: "Role of the message sender.", example: "user" }),
  team: z.string().uuid().openapi({ description: "Team ID associated with the message.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" }),
  tool_call_id: z.string().uuid().nullable().openapi({ description: "Tool call ID associated with the message, if any.", example: "toolcall_123" }),
  tool_calls: z.array(z.any()).nullable().openapi({ description: "Tool calls associated with the message.", example: [] })
}).openapi({ description: "A message in a conversation." })) as any);

export type Invite = Database["public"]["Tables"]["invites"]["Row"];
export const InviteZod = registry.register("Invite", (z.object({
  created_at: z.string().openapi({ description: "Timestamp when the invite was created.", example: "2024-05-01T16:58:00.000Z" }),
  email: z.string().email().openapi({ description: "The email address the invite was sent to.", example: "invitee@example.com" }),
  name: z.string().openapi({ description: "The name of the invitee.", example: "Jane Doe" }),
  team: z.string().uuid().openapi({ description: "The ID of the team the invite is for.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" })
}).openapi({ description: "An invite to join a team." })) as any);

export type Member = Database["public"]["Tables"]["members"]["Row"];
export const MemberZod = registry.register("Member", (z.object({
  account: z.string().uuid().openapi({ description: "The ID of the account that is a member.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  team: z.string().uuid().openapi({ description: "The ID of the team the member belongs to.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" })
}).openapi({ description: "A member of a team." })) as any);

export type Team = Database["public"]["Tables"]["teams"]["Row"];
export const TeamZod = registry.register("Team", (z.object({
  created_at: z.string().nullable().openapi({ description: "Timestamp when the team was created.", example: "2024-05-01T16:58:00.000Z" }),
  email: z.string().email().nullable().openapi({ description: "The team's contact email address.", example: "team@example.com" }),
  id: z.string().uuid().openapi({ description: "Team ID.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" }),
  logo: z.string().nullable().openapi({ description: "URL to the team's logo.", example: "https://example.com/logo.png" }),
  name: z.string().nullable().openapi({ description: "The team's name.", example: "Acme Corp" }),
  phone_number: z.string().nullable().openapi({ description: "The team's contact phone number.", example: "+1234567890" }),
  stripe_id: z.string().nullable().openapi({ description: "Stripe customer ID for the team.", example: "cus_abc123" })
}).openapi({ description: "A team that represents a group of users." })) as any);

export type DataSource = Modify<Database["public"]["Tables"]["datasources"]["Row"], {
  columns: { title: string; description: string; type?: string; hidden?: boolean }[];
  config: any;
  parameters: { id: string; wrap?: string; default: string }[];
  filters: DatasourceFilter[];
  metadata?: Metadata[];
}>;
export const DataSourceZod = registry.register("DataSource", (z.object({
  columns: z.array(z.object({
    title: z.string().openapi({ description: "Column title.", example: "Name" }),
    description: z.string().openapi({ description: "Column description.", example: "The name of the entity." }),
    type: z.string().optional().openapi({ description: "Column type.", example: "text" }),
    hidden: z.boolean().optional().openapi({ description: "Column hidden.", example: false })
  })).openapi({ description: "Datasource columns." }),
  config: z.any().openapi({ description: "Datasource config (arbitrary object)." }),
  created_at: z.string().openapi({ description: "Timestamp when the datasource was created.", example: "2024-05-01T16:58:00.000Z" }),
  created_by: z.string().uuid().nullable().openapi({ description: "ID of the account that created the datasource.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  description: z.string().nullable().openapi({ description: "Description of the datasource.", example: "This datasource contains sales data." }),
  filters: z.array(z.object({
    column: z.string().openapi({ description: "Filter column.", example: "age" }),
    type: z.enum(["text", "number", "date"]).openapi({ description: "Filter type.", example: "number" }),
    label: z.string().openapi({ description: "Filter label.", example: "Age" })
  })).openapi({ description: "Datasource filters." }),
  id: z.string().uuid().openapi({ description: "Datasource ID.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  integration: z.string().uuid().nullable().openapi({ description: "Integration ID associated with the datasource, if any.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  last_updated_at: z.string().openapi({ description: "Timestamp when the datasource was last updated.", example: "2024-05-02T10:00:00.000Z" }),
  last_updated_by: z.string().uuid().nullable().openapi({ description: "ID of the account that last updated the datasource.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  metadata: z.array(MetadataItemZod).openapi({ description: "Datasource metadata." }),
  parameters: z.array(z.object({
    id: z.string().openapi({ description: "Parameter ID.", example: "param1" }),
    wrap: z.string().optional().openapi({ description: "Wrap option for parameter.", example: "quotes" }),
    default: z.string().openapi({ description: "Default value for parameter.", example: "default_value" })
  })).openapi({ description: "Datasource parameters." }),
  size: z.number().openapi({ description: "Size of the datasource.", example: 1000 }),
  source: z.string().openapi({ description: "Source of the datasource.", example: "csv" }),
  team: z.string().uuid().openapi({ description: "Team ID that owns the datasource.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" }),
  title: z.string().openapi({ description: "Title of the datasource.", example: "Sales Data" })
}).openapi({ description: "A datasource that provides data for visualizations." })) as any);

export type EmbedUser = Modify<Database["public"]["Tables"]["embed_users"]["Row"], { parameters: Metadata[] }>;
export const EmbedUserZod = registry.register("EmbedUser", (z.object({
  created_at: z.string().openapi({ description: "Timestamp when the embed user was created.", example: "2024-05-01T16:58:00.000Z" }),
  email: z.string().email().nullable().openapi({ description: "Email address of the embed user.", example: "embeduser@example.com" }),
  id: z.string().uuid().openapi({ description: "Embed user ID.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  last_updated_at: z.string().openapi({ description: "Timestamp when the embed user was last updated.", example: "2024-05-02T10:00:00.000Z" }),
  name: z.string().openapi({ description: "Name of the embed user.", example: "John Doe" }),
  parameters: z.any().nullable().openapi({ description: "Parameters for the embed user (arbitrary object)." }),
  team: z.string().uuid().openapi({ description: "Team ID that the embed user belongs to.", example: "team_123" })
}).openapi({ description: "An embed user." })) as any);

export type Subscription = Database["public"]["Tables"]["subscriptions"]["Row"];
export const SubscriptionZod = registry.register("Subscription", (z.object({
  amount: z.number().openapi({ description: "Subscription amount.", example: 1999 }),
  created_at: z.string().openapi({ description: "Timestamp when the subscription was created.", example: "2024-05-01T16:58:00.000Z" }),
  currency: z.string().openapi({ description: "Currency of the subscription.", example: "USD" }),
  integrations: z.array(z.string()).openapi({ description: "Integrations included in the subscription.", example: ["slack", "github"] }),
  interval: z.string().openapi({ description: "Billing interval.", example: "month" }),
  period_end: z.string().openapi({ description: "End of the current billing period.", example: "2024-06-01T16:58:00.000Z" }),
  period_start: z.string().openapi({ description: "Start of the current billing period.", example: "2024-05-01T16:58:00.000Z" }),
  product_name: z.string().openapi({ description: "Product name of the subscription.", example: "Pro Plan" }),
  stripe_customer_id: z.string().openapi({ description: "Stripe customer ID.", example: "cus_abc123" }),
  stripe_product_id: z.string().openapi({ description: "Stripe product ID.", example: "prod_abc123" }),
  stripe_subscription_id: z.string().openapi({ description: "Stripe subscription ID.", example: "sub_abc123" }),
  team: z.string().uuid().openapi({ description: "Team ID associated with the subscription.", example: "team_123" })
})) as any);

export type SubscriptionPlan = Database["public"]["Tables"]["subscription_plans"]["Row"];
export const SubscriptionPlanZod = registry.register("SubscriptionPlan", (z.object({
  accounts: z.number().nullable().openapi({ description: "Number of accounts allowed in the plan.", example: 10 }),
  automations: z.boolean().openapi({ description: "Whether automations are enabled in the plan.", example: true }),
  connected_datasources: z.number().openapi({ description: "Number of connected datasources allowed.", example: 5 }),
  copilot: z.boolean().openapi({ description: "Whether copilot is enabled in the plan.", example: true }),
  custom_llm: z.boolean().openapi({ description: "Whether custom LLM is enabled in the plan.", example: false }),
  embed_users: z.number().openapi({ description: "Number of embed users allowed.", example: 100 }),
  name: z.string().openapi({ description: "Name of the subscription plan.", example: "Pro" }),
  premium_integrations: z.number().openapi({ description: "Number of premium integrations allowed.", example: 2 })
}).openapi({ description: "A subscription plan." })) as any);

export type APIKey = Database["public"]["Tables"]["api_keys"]["Row"];
export const APIKeyZod = registry.register("APIKey", (z.object({
  created_at: z.string().openapi({ description: "Timestamp when the API key was created.", example: "2024-05-01T16:58:00.000Z" }),
  description: z.string().nullable().openapi({ description: "Description of the API key.", example: "Key for production environment" }),
  id: z.string().uuid().openapi({ description: "API Key ID.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  key: z.string().openapi({ description: "The API key string.", example: "ey.1234567890" }),
  last_used_at: z.string().nullable().openapi({ description: "Timestamp when the API key was last used.", example: "2024-05-02T12:00:00.000Z" }),
  team: z.string().uuid().openapi({ description: "Team ID associated with the API key.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" })
}).openapi({ description: "An API key." })) as any);

export type Widget = Modify<
  Database["public"]["Tables"]["widgets"]["Row"],
  {
    settings?: WidgetSettings;
    messages: WidgetMessage[];
    layouts: {
      lg: { x: number; y: number; w: number; h: number };
      sm?: { x: number; y: number; w: number; h: number };
    };
  }
>;
export const WidgetZod = registry.register("Widget", (z.object({
  settings: z.object({
    css_id: z.string().optional().openapi({ description: "Widget CSS ID.", example: "widget-123" }),
    css_classnames: z.string().optional().openapi({ description: "Widget CSS class names.", example: "widget-class" })
  }).optional().openapi({ description: "Widget settings." }),
  messages: z.array(z.object({
    role: z.enum(["user", "assistant"]).openapi({ description: "Widget message role.", example: "user" }),
    content: z.string().openapi({ description: "Widget message content.", example: "Hello from widget!" })
  })).openapi({ description: "Widget messages." }),
  layouts: z.object({
    lg: z.object({
      x: z.number().openapi({ description: "X position.", example: 0 }),
      y: z.number().openapi({ description: "Y position.", example: 0 }),
      w: z.number().openapi({ description: "Width.", example: 4 }),
      h: z.number().openapi({ description: "Height.", example: 3 })
    }),
    sm: z.object({
      x: z.number().openapi({ description: "X position (sm).", example: 0 }),
      y: z.number().openapi({ description: "Y position (sm).", example: 0 }),
      w: z.number().openapi({ description: "Width (sm).", example: 2 }),
      h: z.number().openapi({ description: "Height (sm).", example: 2 })
    }).optional()
  }).openapi({ description: "Widget layouts." })
}).openapi({ description: "A widget." })) as any);

export type DashboardDatasource = Database["public"]["Tables"]["dashboard_datasources"]["Row"];
export const DashboardDatasourceZod = registry.register("DashboardDatasource", (z.object({
  dashboard: z.string().uuid().openapi({ description: "Dashboard ID associated with the datasource.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  datasource: z.string().uuid().openapi({ description: "Datasource ID associated with the dashboard.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  team: z.string().uuid().openapi({ description: "Team ID associated with the dashboard datasource.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" })
}).openapi({ description: "A dashboard datasource." })) as any);

export type Session = Modify<
  Database["public"]["Tables"]["sessions"]["Row"],
  {
    parameters: Metadata[];
  }
>;
export const SessionZod = registry.register("Session", (z.object({
  created_at: z.string().openapi({ description: "Timestamp when the session was created.", example: "2024-05-01T16:58:00.000Z" }),
  dashboard: z.string().uuid().openapi({ description: "Dashboard ID associated with the session.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  embed_user: z.string().uuid().openapi({ description: "Embed user ID associated with the session.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  parameters: z.any().openapi({ description: "Session parameters (arbitrary object)." }),
  team: z.string().uuid().openapi({ description: "Team ID associated with the session.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" })
}).openapi({ description: "A user session." })) as any);

export type Question = Database["public"]["Tables"]["questions"]["Row"];
export const QuestionZod = registry.register("Question", (z.object({
  created_at: z.string().openapi({ description: "Timestamp when the question was created.", example: "2024-05-01T16:58:00.000Z" }),
  dashboard: z.string().uuid().openapi({ description: "Dashboard ID associated with the question.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  id: z.string().uuid().openapi({ description: "Question ID.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" }),
  messages: z.any().openapi({ description: "Messages associated with the question (arbitrary object)." }),
  query: z.string().openapi({ description: "Query text of the question.", example: "What were last month's sales?" }),
  team: z.string().uuid().nullable().openapi({ description: "Team ID associated with the question, if any.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" })
}).openapi({ description: "A question asked in the dashboard." })) as any);

export type Integration = Modify<
  Database["public"]["Tables"]["integrations"]["Row"],
  {
    config: any;
  }
>;
export const IntegrationZod = registry.register("Integration", (z.object({
  config: z.any().openapi({ description: "Integration config (arbitrary object)." }),
  created_at: z.string().openapi({ description: "Timestamp when the integration was created.", example: "2024-05-01T16:58:00.000Z" }),
  id: z.string().uuid().openapi({ description: "Integration ID.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  provider: z.string().openapi({ description: "Integration provider.", example: "slack" }),
  status: z.string().openapi({ description: "Integration status.", example: "active" }),
  team: z.string().uuid().openapi({ description: "Team ID associated with the integration.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" }),
  title: z.string().nullable().openapi({ description: "Title of the integration.", example: "Slack Integration" })
}).openapi({ description: "An integration with an external service." })) as any);

export type IntegrationTable = Modify<
  Database["public"]["Tables"]["integration_tables"]["Row"],
  {
    fields: any[];
  }
>;
export const IntegrationTableZod = registry.register("IntegrationTable", (z.object({
  id: z.string().uuid().openapi({ description: "Integration ID.", example: "9b7deda3-7e2d-458e-84e9-632e47fbe682" }),
  fields: z.array(z.object({
    name: z.string().openapi({ description: "Field name.", example: "name" }),
    type: z.string().openapi({ description: "Field type.", example: "text" }),
    description: z.string().openapi({ description: "Field description.", example: "The name of the entity." }),
    hidden: z.boolean().optional().openapi({ description: "Field hidden.", example: false })
  })).openapi({ description: "Integration fields." }),
  team: z.string().uuid().openapi({ description: "Team ID associated with the integration table.", example: "92c680a3-a9c4-4c02-bb2d-a5d049789662" }),
  title: z.string().nullable().openapi({ description: "Title of the integration table.", example: "Slack Integration Table" }),
  description: z.string().nullable().openapi({ description: "Description of the integration table.", example: "This integration table is used to connect to Slack." }),
}).openapi({ description: "A table under an integration." })) as any);

export const LLMHostingTypeZod = z.enum(["default", "custom"]).openapi({ description: 'Specifies if the LLM is hosted by Onvo or self-hosted.' });




export type LLMSettings = Database["public"]["Tables"]["llm_settings"]["Row"];
export const LLMSettingsZod = registry.register("LLMSettings", (z.object({
  analyst_agent_type: LLMHostingTypeZod,
  programmer_agent_type: LLMHostingTypeZod,

  custom_api_url: z.string().url().nullable().openapi({ description: 'URL for self-hosted API.', example: 'http://localhost:11434' }),
  custom_api_key: z.string().nullable().openapi({ description: 'API key for custom models (write-only).', example: 'sk-...', format: 'password' }),

  programmer_agent_model: z.string().openapi({ description: 'Model used for the programmer agent.', example: 'openai/gpt-3.5-turbo' }),
  analyst_agent_model: z.string().openapi({ description: 'Model used for the analyst agent.', example: 'openai/gpt-4-turbo' }),

  programmer_agent_prompt: z.string().nullable().openapi({ description: 'Custom prompt for the programmer agent.', example: 'You are a helpful AI assistant specialized in code generation.' }),
  analyst_agent_prompt: z.string().nullable().openapi({ description: 'Custom prompt for the analyst agent.', example: 'You are a helpful AI assistant specialized in data analysis.' }),

  team: z.string().uuid().openapi({ description: 'The ID of the team these settings belong to (read-only).', example: 'd4e5f6a7-b8c9-0123-4567-890abcdef012', readOnly: true }),
  updated: z.boolean().openapi({ description: 'Indicates if the settings have been updated from defaults (read-only).', example: true, readOnly: true }),
}).openapi({ description: "LLM settings." })) as any);

export type Log = Database["public"]["Tables"]["logs"]["Row"];
export const LogZod = registry.register("Log", (z.object({
  account: z.string().uuid().nullable().openapi({ description: 'ID of the account that generated the log, if applicable.', example: 'e5f6a7b8-c9d0-1234-5678-90abcdef0123' }),
  created_at: z.string().datetime().openapi({ description: 'Timestamp when the log was created.', example: '2024-05-01T16:58:00.000Z' }),
  dashboard: z.string().uuid().nullable().openapi({ description: 'ID of the dashboard related to the log, if applicable.', example: 'f6a7b8c9-d0e1-2345-6789-0abcdef01234' }),
  embed_user: z.string().nullable().openapi({ description: 'ID of the embed user related to the log, if applicable.', example: 'embed-user-xyz789' }),
  id: z.string().uuid().openapi({ description: 'The unique identifier for the log entry.', example: '01234567-89ab-cdef-0123-456789abcdef' }),
  team: z.string().uuid().nullable().openapi({ description: 'ID of the team associated with the log.', example: '12345678-9abc-def0-1234-56789abcdef0' }),
  type: z.string().openapi({ description: 'The type or category of the log event.', example: 'widget_interaction' }),
  widget: z.string().uuid().nullable().openapi({ description: 'ID of the widget related to the log, if applicable.', example: '23456789-abcd-ef01-2345-6789abcdef01' })
}).openapi({ description: "A log entry recording an event within the system." })) as any);

// Generate OpenAPI components from Zod schemas
const generator = new OpenApiGeneratorV3(registry.definitions);
export const OpenApiComponents = generator.generateComponents();